import { v4 as uuidv4 } from 'uuid';

// Generate unique request ID for tracing
export const generateRequestId = () => uuidv4();

// Sensitive fields that should never be logged
const SENSITIVE_FIELDS = [
  'password',
  'token',
  'accessToken',
  'refreshToken',
  'apiKey',
  'secret',
  'authorization',
  'cookie',
  'session'
];

// Filter sensitive data from objects
const filterSensitiveData = (data) => {
  if (!data || typeof data !== 'object') return data;
  
  const filtered = Array.isArray(data) ? [...data] : { ...data };
  
  Object.keys(filtered)?.forEach(key => {
    const lowerKey = key?.toLowerCase();
    if (SENSITIVE_FIELDS?.some(field => lowerKey?.includes(field))) {
      filtered[key] = '[REDACTED]';
    } else if (typeof filtered?.[key] === 'object' && filtered?.[key] !== null) {
      filtered[key] = filterSensitiveData(filtered?.[key]);
    }
  });
  
  return filtered;
};

// Log levels
const LOG_LEVELS = {
  ERROR: 'error',
  WARN: 'warn',
  INFO: 'info',
  DEBUG: 'debug'
};

// Structured logger
class Logger {
  constructor() {
    this.requestId = null;
  }

  setRequestId(id) {
    this.requestId = id;
  }

  log(level, message, data = {}) {
    const timestamp = new Date()?.toISOString();
    const logEntry = {
      timestamp,
      level,
      message,
      requestId: this.requestId,
      ...filterSensitiveData(data)
    };

    // In production, send to monitoring service
    // For now, use console with appropriate method
    if (import.meta.env?.MODE === 'production') {
      // Only log errors and warnings in production
      if (level === LOG_LEVELS?.ERROR || level === LOG_LEVELS?.WARN) {
        console[level](JSON.stringify(logEntry));
      }
    } else {
      // Development: log everything
      console[level === LOG_LEVELS?.DEBUG ? 'log' : level](logEntry);
    }
  }

  error(message, error, context = {}) {
    this.log(LOG_LEVELS?.ERROR, message, {
      error: error?.message || error,
      stack: import.meta.env?.MODE !== 'production' ? error?.stack : undefined,
      ...context
    });
  }

  warn(message, context = {}) {
    this.log(LOG_LEVELS?.WARN, message, context);
  }

  info(message, context = {}) {
    this.log(LOG_LEVELS?.INFO, message, context);
  }

  debug(message, context = {}) {
    this.log(LOG_LEVELS?.DEBUG, message, context);
  }

  // Audit trail for sensitive operations
  audit(action, userId, details = {}) {
    this.log(LOG_LEVELS?.INFO, `AUDIT: ${action}`, {
      action,
      userId,
      ...filterSensitiveData(details)
    });
  }
}

export const logger = new Logger();
export default logger;